import { useEffect, useMemo, useState } from 'react';
import { useAppUser } from '../../auth/AppUserContext';
import { getVisibleStores } from '../../data/stores';
import { getAllStoreStatuses, type StoreStatusRow } from '../../data/storeStatus';
import { getPublishedTemplates, type PublishedTemplate } from '../../data/templates';
import { getOpenTasks, type ServerTask } from '../../data/tasks';
import type { AuditRecord } from '../../data/audits';
import { coverWithoutStores, getRecentAudits, summariesCover } from '../../data/districtSummaries';
import { withoutTestStores } from '../../audit/analytics';
import { buildDistrictRows, buildStoreRows, byUrgency, type DistrictRow, type StoreRow } from '../../data/dashboard';
import type { Store } from '../../data/types';
import type { GradesState } from './DashboardTables';

/**
 * Everything the role dashboards read (Home, Reports → Audit compliance): the
 * visible stores, their audit clocks, live templates, open tasks and recent
 * audits, built into board rows + district rows. Moved out of HomePage
 * (Home reorganisation, owner rulings 2026-10-25) so both pages share it.
 */
export function useDashboardData() {
  const { user, summaryDistricts, impersonating } = useAppUser();
  /** Some stale monthly summaries are still being rebuilt (data/districtSummaries.ts). */
  const [refreshing, setRefreshing] = useState(false);
  const [stores, setStores] = useState<Store[] | null>(null);
  // null = still loading. A failed read is recorded in `failed` so grades
  // say "unavailable" instead of showing an inflated number.
  const [statuses, setStatuses] = useState<StoreStatusRow[] | null>(null);
  const [templates, setTemplates] = useState<PublishedTemplate[] | null>(null);
  const [tasks, setTasks] = useState<ServerTask[] | null>(null);
  const [failed, setFailed] = useState<string[]>([]);
  const fail = (what: string) => setFailed((f) => (f.includes(what) ? f : [...f, what]));
  const [audits, setAudits] = useState<AuditRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    // A re-run (summaryDistricts arriving after the saved profile) or leaving
    // the page: the older load must not land (QA 2026-10-22).
    let live = true;
    // The E2E fixture district stays off the company board (kept only when
    // it is all this viewer has — the test account).
    const visible = getVisibleStores(user).then((list) => withoutTestStores(list));
    visible
      .then((list) => live && setStores(list))
      .catch((err: unknown) =>
        live && setError(err instanceof Error ? err.message : String(err)),
      );
    getAllStoreStatuses()
      .then((rows) => live && setStatuses(rows))
      .catch(() => {
        if (!live) return;
        fail('audit status');
        setStatuses([]);
      });
    getPublishedTemplates()
      .then((rows) => live && setTemplates(rows))
      .catch(() => {
        if (!live) return;
        fail('audit templates');
        setTemplates([]);
      });
    getOpenTasks()
      .then((rows) => live && setTasks(rows))
      .catch(() => {
        if (!live) return;
        fail('open tasks');
        setTasks([]);
      });
    // The monthly summaries when they cover every store on the board (owner
    // ruling 2026-10-22), else the audits read as before. "No summaries" and
    // "all districts" don't wait for the store list.
    const decided = coverWithoutStores(summaryDistricts, impersonating);
    const show = (r: { audits: AuditRecord[]; refreshing: boolean }) => {
      if (!live) return;
      setAudits(r.audits);
      setRefreshing(r.refreshing);
    };
    (decided !== null ? Promise.resolve(decided) : visible.then((list) => summariesCover(summaryDistricts, list, impersonating)))
      .then((cover) => getRecentAudits(cover, { onUpdate: show }))
      .then(show)
      .catch(() => {
        if (!live) return;
        fail('completed audits');
        setAudits([]);
      });
    return () => {
      live = false;
    };
  }, [user, summaryDistricts, impersonating]);

  /** Grades need every input; until then the column shows "…" (or "n/a" on a failed read). */
  const gradesState: GradesState =
    failed.length > 0 ? 'unavailable' : tasks === null || audits === null ? 'loading' : 'ready';

  const rows = useMemo<StoreRow[] | null>(() => {
    // The board itself waits for statuses + templates (they decide each row's status).
    if (!user || stores === null || statuses === null || templates === null) return null;
    return buildStoreRows({
      stores,
      statuses,
      templates,
      openTasks: tasks ?? [],
      submittedAudits: audits ?? [],
    }).sort(byUrgency);
  }, [user, stores, statuses, templates, tasks, audits]);

  const districtRows = useMemo<DistrictRow[] | null>(
    () => (rows === null || gradesState !== 'ready' ? null : buildDistrictRows(rows)),
    [rows, gradesState],
  );

  /** Recent submitted audits, limited to the stores this viewer can see. */
  const recentAudits = useMemo(() => {
    if (audits === null || stores === null) return null;
    const byId = new Map(stores.map((st) => [Number(st.id), st]));
    return audits.flatMap((a) => {
      const st = byId.get(a.storeId);
      return st ? [{ audit: a, store: st }] : [];
    });
  }, [audits, stores]);

  return { stores, statuses, templates, tasks, audits, failed, refreshing, error, rows, gradesState, districtRows, recentAudits };
}
